import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ChevronRight, ChevronDown, X, ChevronLeft, Sparkles, ArrowUpRight, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEtkinlikler } from '../../hooks/useSupabaseData';

const CAT_COLORS = {
  'Seminer':   { dot: 'bg-blue-500',    badge: 'text-blue-600 bg-blue-50 ring-blue-200/70' },
  'Tören':     { dot: 'bg-violet-500',  badge: 'text-violet-600 bg-violet-50 ring-violet-200/70' },
  'Sempozyum': { dot: 'bg-emerald-500', badge: 'text-emerald-600 bg-emerald-50 ring-emerald-200/70' },
  'Konferans': { dot: 'bg-orange-500',  badge: 'text-orange-600 bg-orange-50 ring-orange-200/70' },
  'Söyleşi':   { dot: 'bg-indigo-500',  badge: 'text-indigo-600 bg-indigo-50 ring-indigo-200/70' },
  'Sergi':     { dot: 'bg-amber-500',   badge: 'text-amber-600 bg-amber-50 ring-amber-200/70' },
  'Workshop':  { dot: 'bg-pink-500',    badge: 'text-pink-600 bg-pink-50 ring-pink-200/70' },
};
const defaultCat = { dot: 'bg-gray-400', badge: 'text-gray-500 bg-gray-50 ring-gray-200/70' };
const catColor = c => CAT_COLORS[c] || defaultCat;

const parseEventDate = dateStr => {
  try {
    if (!dateStr) return new Date(0);
    if (dateStr.includes('.')) {
      const [d, m, y] = dateStr.split(' ')[0].split('.');
      return new Date(+y, +m - 1, +d);
    }
    return new Date(dateStr);
  } catch { return new Date(0); }
};

// ── Lightbox ─────────────────────────────────────────────────────────────────
const Lightbox = ({ events, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const ev = events[idx];
  const col = catColor(ev.category);

  const prev = useCallback(e => { e.stopPropagation(); setIdx(i => (i - 1 + events.length) % events.length); }, [events.length]);
  const next = useCallback(e => { e.stopPropagation(); setIdx(i => (i + 1) % events.length); }, [events.length]);

  useEffect(() => {
    const handler = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  setIdx(i => (i - 1 + events.length) % events.length);
      if (e.key === 'ArrowRight') setIdx(i => (i + 1) % events.length);
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handler); document.body.style.overflow = ''; };
  }, [events.length, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-10 p-2.5 text-white/60 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all"
      >
        <X size={20} />
      </button>

      <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/40 text-xs bg-white/10 px-3 py-1 rounded-full z-10 backdrop-blur-sm">
        {idx + 1} / {events.length}
      </div>

      {events.length > 1 && (
        <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all">
          <ChevronLeft size={22} />
        </button>
      )}

      <motion.div
        key={idx}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.22 }}
        className="flex flex-col lg:flex-row gap-8 max-w-5xl w-full items-center"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-center flex-shrink-0">
          <img
            src={ev.imageURL}
            alt={ev.baslik}
            className="max-h-[78vh] max-w-full object-contain rounded-2xl shadow-2xl"
          />
        </div>
        <div className="text-white min-w-[200px] max-w-xs">
          {ev.category && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ring-1 mb-5 ${col.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />
              {ev.category}
            </span>
          )}
          <h2 className="text-2xl font-bold leading-tight mb-5">{ev.baslik}</h2>
          <div className="space-y-2.5 text-white/60 text-sm">
            <div className="flex items-center gap-2.5"><Calendar size={14} />{ev.tarih}</div>
            {ev.konum && <div className="flex items-center gap-2.5"><MapPin size={14} />{ev.konum}</div>}
          </div>
          {ev.aciklama && <p className="mt-4 text-white/45 text-sm leading-relaxed line-clamp-4">{ev.aciklama}</p>}
        </div>
      </motion.div>

      {events.length > 1 && (
        <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all">
          <ChevronRight size={22} />
        </button>
      )}
    </motion.div>
  );
};

// ── Kategori Rozeti ───────────────────────────────────────────────────────────
const CategoryBadge = ({ category }) => {
  const col = catColor(category);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${col.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${col.dot}`} />
      {category}
    </span>
  );
};

// ── Küçük Kart ────────────────────────────────────────────────────────────────
const EventCard = ({ event, index, onOpen }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.42, delay: index * 0.08 }}
    whileHover={{ y: -5, transition: { duration: 0.18 } }}
    onClick={() => onOpen(index)}
    className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col"
  >
    {/* Afiş — tam boy, kırpma yok */}
    <div className="bg-gray-50/80 overflow-hidden">
      <img
        src={event.imageURL}
        alt={event.baslik}
        className="w-full h-auto object-contain group-hover:scale-[1.025] transition-transform duration-500"
        style={{ display: 'block' }}
      />
    </div>

    {/* Bilgi */}
    <div className="p-4 flex flex-col flex-1">
      {event.category && <div className="mb-2.5"><CategoryBadge category={event.category} /></div>}
      <h3 className="text-sm font-bold text-gray-800 leading-snug mb-auto group-hover:text-indigo-600 transition-colors duration-200 line-clamp-2">
        {event.baslik}
      </h3>
      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-1.5">
          <Calendar size={11} className="text-gray-300" />
          <span>{event.tarih}</span>
        </div>
        {event.konum && (
          <div className="flex items-center gap-1 ml-2 min-w-0">
            <MapPin size={11} className="text-gray-300 flex-shrink-0" />
            <span className="truncate">{event.konum}</span>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

// ── Kategori Dropdown ─────────────────────────────────────────────────────────
const FilterDropdown = ({ categories, filter, setFilter }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const col = catColor(filter === 'Tümü' ? '' : filter);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2.5 pl-4 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-200 text-sm font-semibold text-gray-700 min-w-[160px] justify-between"
      >
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${filter === 'Tümü' ? 'bg-gray-300' : col.dot}`} />
          <span>{filter}</span>
        </div>
        <ChevronDown size={14} className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.14 }}
            className="absolute right-0 mt-2 w-52 bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-gray-200/60 overflow-hidden z-30 py-1.5"
          >
            {categories.map(cat => {
              const c = catColor(cat === 'Tümü' ? '' : cat);
              const active = cat === filter;
              return (
                <button
                  key={cat}
                  onClick={() => { setFilter(cat); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all duration-150 ${
                    active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cat === 'Tümü' ? 'bg-gray-300' : c.dot}`} />
                  <span className="flex-1 text-left">{cat}</span>
                  {active && <Check size={13} className="text-indigo-500 flex-shrink-0" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Ana Bileşen ───────────────────────────────────────────────────────────────
const EventsSection = () => {
  const { etkinlikler } = useEtkinlikler();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [filter, setFilter] = useState('Tümü');

  const events = useMemo(() => (
    etkinlikler
      .filter(e => e.baslik && e.tarih && e.resim_url)
      .map(e => ({ ...e, baslik: e.baslik.trim(), imageURL: e.resim_url, parsedDate: parseEventDate(e.tarih) }))
      .sort((a, b) => b.parsedDate - a.parsedDate)
  ), [etkinlikler]);

  const categories = useMemo(() =>
    ['Tümü', ...new Set(events.map(e => e.category).filter(Boolean))],
    [events]
  );

  const filtered = useMemo(() =>
    filter === 'Tümü' ? events : events.filter(e => e.category === filter),
    [events, filter]
  );

  const featured = filtered[0];
  const rest     = filtered.slice(1, 7);

  const openLightbox  = useCallback(idx => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  if (!events.length) return null;

  return (
    <section className="relative bg-gradient-to-b from-slate-50 via-indigo-50/40 to-slate-50 py-24 overflow-hidden">
      {/* Dekoratif blur lekeleri */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-indigo-100/40 via-purple-50/20 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-100/30 to-transparent rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* ── Başlık ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14"
        >
          <div>
            <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-5">
              <Sparkles size={13} className="text-indigo-500" />
              <span className="text-indigo-600 text-xs font-semibold tracking-widest uppercase">Etkinlikler</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight">
              Yaklaşan{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Etkinliklerimiz
              </span>
            </h2>
          </div>

          {/* Kategori filtresi */}
          {categories.length > 1 && (
            <FilterDropdown categories={categories} filter={filter} setFilter={setFilter} />
          )}
        </motion.div>

        {/* ── Öne Çıkan Etkinlik ── */}
        {featured && (
          <motion.div
            key={featured.baslik}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onClick={() => openLightbox(0)}
            className="group cursor-pointer mb-10 rounded-2xl overflow-hidden border border-gray-100 shadow-md hover:shadow-xl transition-all duration-500 grid lg:grid-cols-2 bg-white"
          >
            {/* Afiş — tam boy */}
            <div className="bg-gray-50/60 flex items-center justify-center overflow-hidden min-h-[200px]">
              <img
                src={featured.imageURL}
                alt={featured.baslik}
                className="w-full h-auto object-contain group-hover:scale-[1.018] transition-transform duration-700"
                style={{ display: 'block', maxHeight: '340px' }}
              />
            </div>

            {/* Bilgi Paneli */}
            <div className="p-6 md:p-8 flex flex-col justify-between bg-gradient-to-br from-gray-50/60 to-indigo-50/40">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  {featured.category && <CategoryBadge category={featured.category} />}
                  <span className="text-xs text-gray-400 font-medium tracking-wide">Öne Çıkan</span>
                </div>

                <h3 className="text-xl md:text-2xl font-black text-gray-900 leading-tight mb-5 group-hover:text-indigo-700 transition-colors duration-300">
                  {featured.baslik}
                </h3>

                <div className="space-y-2.5 mb-4">
                  <div className="flex items-center gap-2.5 text-gray-500 text-sm">
                    <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 border border-indigo-100">
                      <Calendar size={13} className="text-indigo-500" />
                    </div>
                    <span>{featured.tarih}</span>
                  </div>
                  {featured.konum && (
                    <div className="flex items-center gap-2.5 text-gray-500 text-sm">
                      <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 border border-purple-100">
                        <MapPin size={13} className="text-purple-500" />
                      </div>
                      <span>{featured.konum}</span>
                    </div>
                  )}
                </div>

                {featured.aciklama && (
                  <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">{featured.aciklama}</p>
                )}
              </div>

              <div className="mt-6 inline-flex items-center gap-2 text-indigo-600 font-semibold text-sm">
                <span>Afişi Görüntüle</span>
                <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center group-hover:bg-indigo-600 transition-colors duration-300">
                  <ArrowUpRight size={14} className="text-indigo-600 group-hover:text-white transition-colors duration-300" />
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Diğer Etkinlikler ── */}
        {rest.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-14">
            {rest.map((event, i) => (
              <EventCard
                key={event.baslik + i}
                event={event}
                index={i}
                onOpen={idx => openLightbox(idx + 1)}
              />
            ))}
          </div>
        )}

        {/* ── CTA ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <Link
            to="/etkinlikler"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 hover:-translate-y-0.5"
          >
            <Calendar size={17} />
            <span>Tüm Etkinlikleri Gör</span>
            <ChevronRight size={17} />
          </Link>
          <p className="text-gray-400 text-sm mt-4">Tüm etkinlikler tarih ve kategoriye göre filtrelenebilir</p>
        </motion.div>
      </div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            events={filtered.slice(0, 8)}
            startIndex={lightboxIndex}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default EventsSection;
